/**
 * Asset Info Card Component
 * Displays Asset profile information and key metrics
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid,
  Divider,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Block as SuspendIcon,
  CheckCircle as ActivateIcon,
} from '@mui/icons-material';
import { formatDateTime } from '../../../../utils/dateUtils';
import type { UserDetail } from '../../../../types/user';

interface AssetInfoCardProps {
  assetDetail: UserDetail;
  onSuspend?: () => void;
  onActivate?: () => void;
}

export const AssetInfoCard: React.FC<AssetInfoCardProps> = ({
  assetDetail,
  onSuspend,
  onActivate
}) => {
  const theme = useTheme();

  const getAvatarInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getStatusColor = (status: string, theme: any): string => {
    switch (status) {
      case 'active':
        return theme.palette.success.main;
      case 'disabled':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'suspended':
        return theme.palette.grey[500];
      default:
        return theme.palette.text.secondary;
    }
  };

  // Use API-provided mission statistics if available, otherwise fallback to assetMissions
  const totalMissions = assetDetail.totalMissions !== undefined 
    ? assetDetail.totalMissions 
    : (assetDetail.assetMissions?.length || 0);
  const completedMissions = assetDetail.completedMissions !== undefined
    ? assetDetail.completedMissions
    : (assetDetail.assetMissions?.filter((m) => m.status === 'completed').length || 0);
  const showActivateAction = assetDetail.status === 'suspended' && !!onActivate;
  const showSuspendAction = assetDetail.status !== 'suspended' && !!onSuspend;
  const showActionButton = showActivateAction || showSuspendAction;

  return (
    <Card sx={{ marginBottom: theme.spacing(3) }}>
      <CardContent>
        {/* Avatar and Basic Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: theme.spacing(3) }}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              marginBottom: theme.spacing(2),
              backgroundColor: theme.palette.primary.main,
              fontSize: '3rem',
              fontWeight: 600,
              boxShadow: `0 4px 12px rgba(74, 144, 226, 0.3)`,
            }}
            src={assetDetail.avatar}
          >
            {!assetDetail.avatar && getAvatarInitials(assetDetail.name)}
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(0.75),
              fontSize: '1.5rem',
              letterSpacing: '-0.01em',
            }}
          >
            {assetDetail.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
              fontSize: '0.875rem',
            }}
          >
            {assetDetail.email}
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip
              label="Asset"
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 26,
                borderRadius: theme.shapes.borderRadius.md,
                padding: theme.spacing(0, 1.25),
              }}
            />
            <Chip
              label={assetDetail.status === 'active' ? 'Active' : assetDetail.status}
              size="small"
              sx={{
                backgroundColor: getStatusColor(assetDetail.status, theme),
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 26,
                borderRadius: theme.shapes.borderRadius.md,
                padding: theme.spacing(0, 1.25),
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ marginBottom: theme.spacing(2) }} />

        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ marginBottom: theme.spacing(2) }}>
          <Grid item xs={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                Total Missions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '1.125rem',
                }}
              >
                {totalMissions}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                Completed
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '1.125rem',
                }}
              >
                {completedMissions}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ marginBottom: theme.spacing(2) }} />

        {/* User Information */}
        <Grid container spacing={2} sx={{ marginBottom: theme.spacing(2) }}>

          <Grid item xs={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                Join Date:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                {formatDateTime(assetDetail.createdAt)}
              </Typography>
            </Box>
          </Grid>
          {assetDetail.personalInfo?.phone && (
            <Grid item xs={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                    display: 'block',
                  }}
                >
                  Phone:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {assetDetail.personalInfo.phone}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                Last Login:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                {formatDateTime(assetDetail.lastLogin)}
              </Typography>
            </Box>
          </Grid>
          {assetDetail.personalInfo?.address && (
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                    display: 'block',
                  }}
                >
                  Address:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {assetDetail.personalInfo.address}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Payment Methods Count */}
        {assetDetail.paymentMethods && assetDetail.paymentMethods.length > 0 && (
          <Box sx={{ marginTop: theme.spacing(2) }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
                marginBottom: theme.spacing(0.5),
                display: 'block',
              }}
            >
              Payment Methods:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {assetDetail.paymentMethods.length} method{assetDetail.paymentMethods.length !== 1 ? 's' : ''} configured
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        {showActionButton && (
          <>
            <Divider sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }} />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {showActivateAction ? (
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<ActivateIcon />}
                  onClick={onActivate}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  Activate User
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<SuspendIcon />}
                  onClick={onSuspend}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }}
                >
                  Suspend User
                </Button>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

